import { useQueryClient } from "@tanstack/react-query";
import { notification } from "antd";
import { journalsQueries } from "@/entities/journal";
import { $journalApi } from "@/entities/journal/fetch/fetch-client";
import { NOTIFICATION_MESSAGES } from "@/shared/utils";

type SectionTwoControllerOptions = {
  onSendToSignBatchSuccess?: () => void;
};

export const useSectionTwoController = (
  sectionTwoListId: string,
  sectionTwoId: string,
  options?: SectionTwoControllerOptions,
) => {
  const queryClient = useQueryClient();

  const invalidate = () => {
    const listKey = journalsQueries.getSection2({ sectionTwoId }).queryKey;
    queryClient.invalidateQueries(
      { queryKey: [listKey[0], listKey[1]] },
      { cancelRefetch: false },
    );
  };

  const invalidateAfterBulkSend = () => {
    const listKey = journalsQueries.getSection2({ sectionTwoId }).queryKey;
    queryClient.invalidateQueries({
      queryKey: [listKey[0], listKey[1]],
      refetchType: "none",
    });
    queryClient.invalidateQueries(
      {
        queryKey: journalsQueries.getSection2({
          sectionTwoId,
          status: "AWAITING_SIGNATURE",
          page: 1,
          limit: 1000,
        }).queryKey,
        exact: true,
      },
      { cancelRefetch: false },
    );
  };

  const rejectSignEntry = $journalApi.useMutation(
    "post",
    "/api/v1/journals/section-two/{sectionTwoListId}/items/{itemNoVersionId}/status/signerRejected",
  );

  const statusReturnEntry = $journalApi.useMutation(
    "post",
    "/api/v1/journals/section-two/{sectionTwoListId}/items/{itemNoVersionId}/status/inputterReturn",
  );

  const returnEntry = $journalApi.useMutation(
    "post",
    "/api/v1/journals/section-two/{sectionTwoListId}/items/{itemNoVersionId}/return",
  );

  const sendToSignItem = $journalApi.useMutation(
    "post",
    "/api/v1/journals/section-two/{sectionTwoListId}/items/{itemNoVersionId}/sendToSignItem",
  );

  const sendToSignItems = $journalApi.useMutation(
    "post",
    "/api/v1/journals/section-two/{sectionTwoListId}/items/sendToSignItems",
  );

  const handleReject = ({
    itemNoVersionId,
    comment,
    signerEmployeeId,
  }: {
    itemNoVersionId: string;
    comment: string;
    signerEmployeeId: string;
  }) => {
    rejectSignEntry.mutate(
      {
        params: {
          path: { sectionTwoListId, itemNoVersionId },
        },
        body: {
          comment,
          signer: { signerEmployeeId: signerEmployeeId || "" },
        },
      },
      {
        onSuccess: () => {
          notification.success({ message: NOTIFICATION_MESSAGES.ENTRY_REJECTED_SUCCESS }); // "Запись отклонена"
          invalidate();
        },
        onError: () => {
          notification.error({ message: NOTIFICATION_MESSAGES.ENTRY_REJECT_ERROR }); // "Ошибка при отклонении"
        },
      },
    );
  };

  const handleStatusReturn = ({
    itemNoVersionId,
  }: {
    itemNoVersionId: string;
  }) => {
    statusReturnEntry.mutate(
      {
        params: {
          path: { sectionTwoListId, itemNoVersionId },
        },
      },
      {
        onSuccess: () => {
          notification.success({ message: NOTIFICATION_MESSAGES.ENTRY_RECALLED_FROM_SIGNING_SUCCESS }); // "Запись отозвана с подписания"
          invalidate();
        },
        onError: () => {
          notification.error({ message: NOTIFICATION_MESSAGES.ENTRY_RECALL_ERROR }); // "Ошибка при отзыве"
        },
      },
    );
  };

  const handleReturn = ({ itemNoVersionId }: { itemNoVersionId: string }) => {
    returnEntry.mutate(
      {
        params: {
          path: { sectionTwoListId, itemNoVersionId },
        },
        // @ts-expect-error body не совпадает с openapi типами мутации
        body: {},
      },
      {
        onSuccess: () => {
          notification.success({ message: NOTIFICATION_MESSAGES.ENTRY_RETURNED_TO_NEW }); // "Запись возвращена в статус NEW"
          invalidate();
        },
        onError: () => {
          notification.error({ message: NOTIFICATION_MESSAGES.ENTRY_RETURN_ERROR }); // "Ошибка при возврате записи"
        },
      },
    );
  };

  const handleSendToSign = ({
    itemNoVersionId,
  }: {
    itemNoVersionId: string;
  }) => {
    sendToSignItem.mutate(
      {
        params: {
          path: { sectionTwoListId, itemNoVersionId },
        },
      },
      {
        onSuccess: () => {
          notification.success({ message: NOTIFICATION_MESSAGES.ENTRY_SENT_TO_SIGNING }); // "Отправлено на подписание"
          invalidate();
        },
        onError: () => {
          notification.error({ message: NOTIFICATION_MESSAGES.ENTRY_SEND_TO_SIGNING_ERROR_ALT }); // "Не удалось отправить на подписание"
        },
      },
    );
  };

  const handleSendToSignBatch = (
    items: { itemNoVersionId: string; selected: boolean }[],
  ) => {
    sendToSignItems.mutate(
      {
        params: { path: { sectionTwoListId } },
        body: {
          itemsIds: items,
        },
      },
      {
        onSuccess: () => {
          notification.success({ message: NOTIFICATION_MESSAGES.ENTRY_BULK_SENT_SUCCESS }); // "Групповая отправка выполнена"
          invalidateAfterBulkSend();
          options?.onSendToSignBatchSuccess?.();
        },
        onError: () => {
          notification.error({ message: NOTIFICATION_MESSAGES.ENTRY_BULK_SEND_ERROR }); // "Ошибка при групповой отправке"
        },
      },
    );
  };

  return {
    handleReject,
    handleStatusReturn,
    handleReturn,
    handleSendToSign,
    handleSendToSignBatch,
  };
};
